import React, { useState, useEffect, useRef } from "react";
import { FlagIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// Icons for CTA section
const ShieldCheckIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const HeartSolidIcon = (props) => (
  <svg {...props} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const ArrowRightIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const ClockIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const HeartIcon = (props) => (
  <svg {...props} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  const API_BASE_URL = "https://api.donatepe.org";
  const defaultCampaignImage = "/assets/temp_image.png";

  const handleCardClick = (campaignSlug) => {
    navigate(`/campaign/${campaignSlug}`);
  };

  const handleShowMore = () => {
    navigate('/campaigns');
  };

  // Function to check if URL is valid (not localhost and not blob)
  const isValidImageUrl = (url) => {
    if (!url) return false;
    // Check for localhost URLs
    if (url.includes('localhost') || url.includes('127.0.0.1')) return false;
    // Check for blob URLs
    if (url.startsWith('blob:')) return false;
    // Check for invalid uploads
    if (url.includes('blob:http')) return false;
    return true;
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://api.donatepe.org/public/fundraisers");
        const data = await response.json();

        if (data.success && data.fundraisers) {
          // Filter campaigns: only show if status is 'approved' AND show_on_homescreen is 'yes'
          const filteredCampaigns = data.fundraisers.filter(
            (fundraiser) => 
              fundraiser.status === "approved" && 
              fundraiser.show_on_homescreen === "yes"
          );

          const transformedCampaigns = filteredCampaigns.map((fundraiser) => {
            const progress =
              fundraiser.goal_amount > 0
                ? Math.min(
                    100,
                    Math.round((fundraiser.collected_amount / fundraiser.goal_amount) * 100)
                  )
                : 0;

            const getCategory = () => {
              const title = fundraiser?.title?.toLowerCase() || "";
              if (title.includes("medical") || title.includes("cure") || title.includes("cancer") || title.includes("treatment"))
                return "Medical";
              if (title.includes("education") || title.includes("school") || title.includes("college") || title.includes("student"))
                return "Education";
              if (title.includes("animal") || title.includes("pet") || title.includes("dog") || title.includes("cat"))
                return "Animals";
              if (title.includes("emergency") || title.includes("relief") || title.includes("flood") || title.includes("disaster"))
                return "Emergency";
              return "Community";
            };

            let imageUrl = defaultCampaignImage;
            if (fundraiser.cover_image && isValidImageUrl(fundraiser.cover_image)) {
              if (fundraiser.cover_image.startsWith("/uploads/")) {
                imageUrl = `${API_BASE_URL}${fundraiser.cover_image}`;
              } else if (fundraiser.cover_image.startsWith("http")) {
                imageUrl = fundraiser.cover_image;
              } else {
                imageUrl = defaultCampaignImage;
              }
            }

            const slug = fundraiser.slug || `campaign-${fundraiser.id}`;

            let description = "Help us make a difference";
            if (slug) {
              try {
                const generatedDesc = slug.replace(/-/g, " ").replace(/\d+/g, "").trim();
                if (generatedDesc) {
                  description = generatedDesc.charAt(0).toUpperCase() + generatedDesc.slice(1);
                }
              } catch (e) {}
            }

            const category = getCategory();

            const getCategoryIcon = () => {
              if (category === "Medical") return "🏥";
              if (category === "Education") return "📚";
              if (category === "Animals") return "🐾";
              if (category === "Emergency") return "⚡";
              return "🤝";
            };

            const getCategoryColor = () => {
              if (category === "Medical") return "from-rose-500 to-red-500";
              if (category === "Education") return "from-blue-500 to-indigo-500";
              if (category === "Animals") return "from-teal-500 to-emerald-500";
              if (category === "Emergency") return "from-amber-500 to-orange-500";
              return "from-purple-500 to-pink-500";
            };

            return {
              id: fundraiser.id,
              title: fundraiser.title || "Help us make a difference",
              description: description,
              category: category,
              categoryIcon: getCategoryIcon(),
              categoryColor: getCategoryColor(),
              raised: fundraiser.collected_amount || 0,
              goal: fundraiser.goal_amount || 0,
              imageUrl: imageUrl,
              isVerified: fundraiser.collected_amount > 0,
              progress: progress,
              slug: slug,
              creatorName: fundraiser.creator_name || "Anonymous",
              createdAt: fundraiser.created_at,
            };
          });

          setCampaigns(transformedCampaigns);
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const scrollLeft = () => {
    if (sliderRef.current) {
      const cardWidth = window.innerWidth < 768 ? window.innerWidth - 48 : 380;
      sliderRef.current.scrollBy({ left: -cardWidth - 24, behavior: "smooth" });
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      const cardWidth = window.innerWidth < 768 ? window.innerWidth - 48 : 380;
      sliderRef.current.scrollBy({ left: cardWidth + 24, behavior: "smooth" });
      setCurrentIndex((prev) =>
        Math.min(prev + 1, campaigns.length - (window.innerWidth < 768 ? 1 : 3))
      );
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return "Recently";
    
    const campaignDate = new Date(dateString);
    const today = new Date();
    
    const campaignDay = new Date(campaignDate.getFullYear(), campaignDate.getMonth(), campaignDate.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    const diffTime = todayDay - campaignDay;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const cardsToShow = window.innerWidth < 768 ? 1 : 3;
  const displayedCampaigns = campaigns.slice(currentIndex, currentIndex + cardsToShow);

  if (loading) {
    return (
      <section className="relative bg-white py-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-gray-100 rounded-3xl h-[400px]"
              >
                <div className="animate-pulse w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-3xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="campaigns-section" className="relative bg-white py-0 overflow-hidden">
      {/* BACKGROUND */}
      <div className="absolute inset-0">
        <div className="absolute top-20 -left-20 w-[600px] h-[600px] bg-gradient-to-br from-teal-100/30 to-emerald-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-100/30 to-indigo-100/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-[400px] h-[400px] bg-amber-100/20 rounded-full blur-3xl"></div>

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #14b8a6 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 pb-0">
        {/* HEADER - CENTERED SUBTEXT */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-3">
            Stories that{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
              need you
            </span>
          </h2>

          <p className="text-base text-gray-500 max-w-xl mx-auto">
            Every campaign is a heartbeat away from reaching its goal.
            <span className="block text-teal-600 font-medium mt-1">
              Your support makes it possible.
            </span>
          </p>
        </div>

        {/* CARDS */}
        {campaigns.length > 0 ? (
          <>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
              ref={sliderRef}
            >
              {displayedCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="cursor-pointer"
                  onClick={() => handleCardClick(campaign.slug)}
                >
                  <div className="bg-white rounded-3xl overflow-hidden shadow-xl h-[420px] flex flex-col border border-white/40">

                    {/* IMAGE */}
                    <div className="relative h-44 lg:h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={campaign.imageUrl}
                        alt={campaign.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = defaultCampaignImage;
                        }}
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/30 to-transparent"></div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/40"></div>

                      <div className="absolute top-3 left-3 flex flex-col gap-2">
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-white/95 backdrop-blur-sm rounded-full text-[10px] font-medium text-gray-700 shadow-xl">
                          <span>{campaign.categoryIcon}</span>
                          <span>{campaign.category}</span>
                        </div>
                      </div>

                      <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
                        {campaign.isVerified && (
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-white/95 backdrop-blur-sm rounded-full shadow-xl">
                            <FlagIcon className="w-3 h-3 text-emerald-600" />
                            <span className="text-[8px] font-bold text-emerald-700">
                              VERIFIED
                            </span>
                          </div>
                        )}
                      </div>

                      {/* CREATOR NAME */}
                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-[10px] font-bold shadow-2xl border-2 border-white/80">
                          {campaign.creatorName[0]?.toUpperCase() || "U"}
                        </div>

                        <div className="text-white">
                          <p className="text-[10px] font-bold drop-shadow-lg">
                            {campaign.creatorName}
                          </p>
                          <p className="text-[7px] text-white/90">
                            {getTimeAgo(campaign.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* CONTENT */}
                    <div className="p-3 flex flex-col flex-grow bg-white">
                      {/* MOBILE DETAILS CARD AFTER IMAGE */}
                      <div className="md:hidden bg-white border border-gray-100 rounded-2xl shadow-sm p-2 mb-2">
                        <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2">
                          {campaign.title}
                        </h3>

                        <p className="text-[10px] text-gray-600 mb-2 line-clamp-2">
                          {campaign.description}
                        </p>

                        <div className="flex justify-between items-end">
                          <div>
                            <span className="text-base font-bold text-gray-900">
                              {formatCurrency(campaign.raised)}
                            </span>
                            <span className="text-[10px] text-gray-500 ml-1">
                              of {formatCurrency(campaign.goal)}
                            </span>
                          </div>

                          <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg">
                            {campaign.progress}%
                          </span>
                        </div>
                      </div>

                      {/* DESKTOP TITLE + DESC */}
                      <div className="hidden md:block">
                        <h3 className="text-sm lg:text-base font-bold text-gray-900 mb-1 line-clamp-2">
                          {campaign.title}
                        </h3>

                        <p className="text-[10px] text-gray-600 mb-2 line-clamp-2 flex-grow">
                          {campaign.description}
                        </p>
                      </div>

                      <div className="space-y-2 mt-auto">
                        <div className="hidden md:flex justify-between items-end">
                          <div>
                            <span className="text-base lg:text-lg font-bold text-gray-900">
                              {formatCurrency(campaign.raised)}
                            </span>
                            <span className="text-[10px] text-gray-500 ml-1">
                              of {formatCurrency(campaign.goal)}
                            </span>
                          </div>

                          <div className="relative">
                            <div className="absolute inset-0 bg-teal-100 rounded-full blur-md opacity-50"></div>
                            <span className="relative text-sm font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-lg">
                              {campaign.progress}%
                            </span>
                          </div>
                        </div>

                        <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="absolute top-0 left-0 h-full rounded-full"
                            style={{
                              width: `${campaign.progress}%`,
                              background: `linear-gradient(90deg, ${
                                campaign.categoryColor.includes("rose")
                                  ? "#f43f5e"
                                  : campaign.categoryColor.includes("blue")
                                  ? "#3b82f6"
                                  : campaign.categoryColor.includes("teal")
                                  ? "#14b8a6"
                                  : campaign.categoryColor.includes("amber")
                                  ? "#f59e0b"
                                  : "#8b5cf6"
                              }, ${
                                campaign.categoryColor.includes("rose")
                                  ? "#ef4444"
                                  : campaign.categoryColor.includes("blue")
                                  ? "#6366f1"
                                  : campaign.categoryColor.includes("teal")
                                  ? "#10b981"
                                  : campaign.categoryColor.includes("amber")
                                  ? "#f97316"
                                  : "#d946ef"
                              })`,
                            }}
                          />
                        </div>

                        {/* Almost there badge */}
                        <div className="flex items-center justify-end text-[10px] pt-0.5">
                          {campaign.progress > 75 && (
                            <div className="flex items-center gap-1 text-[8px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                              <SparklesIcon className="w-2.5 h-2.5" />
                              <span>Almost there!</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* DONATE BUTTON */}
                      <div className="mt-3 pt-2 border-t border-gray-100 flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(campaign.slug);
                          }}
                          className="w-2/4 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full text-sm font-semibold shadow-lg"
                        >
                          Donate now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* SHOW MORE BUTTON - Added at the right end */}
            <div className="flex justify-end mt-8">
              <button
                onClick={handleShowMore}
                className="group flex items-center gap-2 px-6 py-2.5 bg-white border-2 border-teal-500 text-teal-600 rounded-full text-sm font-semibold hover:bg-teal-500 hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <span>Show More Campaigns</span>
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
              <span className="text-3xl">✨</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No campaigns found
            </h3>
            <p className="text-gray-500">Check back soon for new stories</p>
          </div>
        )}

        {/* ARROWS */}
        {campaigns.length > cardsToShow && (
          <>
            <div className="hidden md:flex items-center justify-end gap-4 mt-4">
              <div className="flex items-center gap-2 mr-4">
                <span className="text-sm text-gray-400">
                  0{currentIndex + 1}
                </span>
                <span className="text-xs text-gray-300">/</span>
                <span className="text-sm font-bold text-gray-900">
                  0{Math.max(1, campaigns.length - 2)}
                </span>
              </div>

              <button
                onClick={scrollLeft}
                disabled={currentIndex === 0}
                className="w-12 h-12 rounded-full bg-white border-2 border-gray-100 shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-4 h-4 mx-auto text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={scrollRight}
                disabled={currentIndex >= campaigns.length - 3}
                className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-4 h-4 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="flex md:hidden items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">0{currentIndex + 1}</span>
                <span className="text-xs text-gray-300">/</span>
                <span className="text-xs font-bold text-gray-900">{campaigns.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={scrollLeft}
                  disabled={currentIndex === 0}
                  className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md disabled:opacity-40 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={scrollRight}
                  disabled={currentIndex >= campaigns.length - 1}
                  className="w-10 h-10 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md disabled:opacity-40 flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex md:hidden items-center justify-center gap-1.5 mt-3">
              {Array.from({ length: Math.min(5, campaigns.length) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "w-6 bg-gradient-to-r from-teal-600 to-emerald-600"
                      : "w-1 bg-gray-300"
                  }`}
                />
              ))}
              {campaigns.length > 5 && (
                <span className="text-xs text-gray-400 ml-1">+{campaigns.length - 5}</span>
              )}
            </div>
          </>
        )}

        {/* NO BOTTOM PADDING */}
        <div className="text-center mt-0 pb-0">
          <p className="text-xs text-gray-400 mt-3 flex items-center justify-center gap-2">
            <span className="w-1 h-1 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="italic">New campaigns added daily</span>
            <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></span>
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes pulse-slower {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.4;
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-20px) scale(1.05);
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
        .animate-pulse-slower {
          animation: pulse-slower 8s ease-in-out infinite;
        }
        .animate-float {
          animation: float 12s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Campaigns;